import React, { useState, useEffect } from "react";
import { UMSSBlueDark } from "../utils/colors";
import { collection, query, where, getDocs } from "firebase/firestore";
import { app, db } from "../firebase";
import { parseFlexibleDob, formatDobForDisplay } from "../utils/parseDob";

// PatientMatchModal component to display when a match is found
const PatientMatchModal = ({ matches, onClose, onVerify }) => {
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  
  const handleVerify = () => {
    const selectedMatch = matches.find(match => match.id === selectedMatchId);
    if (selectedMatch) {
      onVerify(selectedMatch);
    }
    onClose();
  };
  
  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: "#fff",
        borderRadius: "12px",
        padding: "1.5rem",
        width: "90%",
        maxWidth: "600px",
        maxHeight: "90vh",
        overflow: "auto",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)"
      }}>
        <h2 style={{ marginTop: 0 }}>Existing Patient Record Found</h2>
        <p>We found the following matching record(s) in our system. Is this you?</p>
        
        {matches.map(match => (
          <div 
            key={match.id} 
            style={{
              padding: "1rem",
              border: `2px solid ${selectedMatchId === match.id ? UMSSBlueDark : "#ddd"}`,
              borderRadius: "8px",
              marginBottom: "1rem",
              cursor: "pointer"
            }}
            onClick={() => setSelectedMatchId(match.id)}
          >
            <h3 style={{ margin: "0 0 0.5rem" }}>{match.firstName} {match.lastName}</h3>
            <p style={{ margin: "0.3rem 0" }}>DOB: {match.dob}</p>
            <p style={{ margin: "0.3rem 0" }}>Email: {match.email}</p>
            <p style={{ margin: "0.3rem 0" }}>Phone: {match.phone}</p>
            <p style={{ margin: "0.3rem 0", fontStyle: "italic", color: "#666" }}>
              Matched by: {match.matchType}
            </p>
          </div>
        ))}
        
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "1rem" }}>
          <button 
            onClick={onClose}
            style={{
              padding: "0.7rem 1.5rem",
              borderRadius: "8px",
              border: "1px solid #ccc",
              backgroundColor: "#f3f3f3",
              cursor: "pointer"
            }}
          >
            No, I'm a new patient
          </button>
          <button 
            onClick={handleVerify}
            disabled={!selectedMatchId}
            style={{
              padding: "0.7rem 1.5rem",
              borderRadius: "8px",
              border: "none",
              backgroundColor: selectedMatchId ? UMSSBlueDark : "#ccc",
              color: "#fff",
              cursor: selectedMatchId ? "pointer" : "not-allowed"
            }}
          >
            Yes, this is me
          </button>
        </div>
      </div>
    </div>
  );
};

function BasicInfo({ basicInfo, onChange, onValidityChange, onCheckPatientMatches }) {
  const [activeDomain, setActiveDomain] = useState("");
  const [ageError, setAgeError] = useState(false);
  const [formattedDob, setFormattedDob] = useState("");
  const [possibleMatches, setPossibleMatches] = useState([]);
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [verifiedPatient, setVerifiedPatient] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [dobText, setDobText] = useState("");
  const [dobTypeError, setDobTypeError] = useState("");

  // Helper to check if user is at least 18 years old
  const validateAge = (dob) => {
    if (!dob) return false;
    
    const birthDate = new Date(dob);
    const today = new Date();
    
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age >= 18;
  };

  
  // Format date to MM/DD/YYYY for display
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const [yyyy, mm, dd] = dateString.split("-");
    
    // Convert to numbers so we remove leading zeros if desired
    const month = parseInt(mm, 10); 
    const day = parseInt(dd, 10); 
    const year = parseInt(yyyy, 10);
  
    return `${month}/${day}/${year}`;
  };

  // Keep display in sync when DOB is set from outside (e.g. match verification)
  useEffect(() => {
    if (basicInfo.dob) {
      setDobText(formatDobForDisplay(basicInfo.dob));
    }
  }, [basicInfo.dob]);

  // Update validation state when DOB changes
  useEffect(() => {
    if (basicInfo.dob) {
      const isValid = validateAge(basicInfo.dob);
      setAgeError(!isValid);
      if (onValidityChange) {
        onValidityChange(isValid);
      }
      setFormattedDob(formatDate(basicInfo.dob));
    } else {
      setFormattedDob("");
      setAgeError(false);
      if (onValidityChange) {
        onValidityChange(false);
      }
    }
  }, [basicInfo.dob, onValidityChange]);

  const handleDobTextChange = (e) => {
    const v = e.target.value;
    setDobText(v);
    const r = parseFlexibleDob((v || "").trim());
    if (r.ok) {
      setDobTypeError("");
      onChange({ ...basicInfo, dob: r.iso });
    } else {
      onChange({ ...basicInfo, dob: "" });
    }
  };

  const handleDobBlur = () => {
    const t = dobText.trim();
    if (!t) {
      setDobTypeError("");
      return;
    }
    const r = parseFlexibleDob(t);
    if (!r.ok) {
      setDobTypeError(r.error);
    } else {
      setDobTypeError("");
    }
  };

  const handleDobPickerChange = (e) => {
    const v = e.target.value;
    if (v) {
      setDobTypeError("");
      setDobText(formatDobForDisplay(v));
      onChange({ ...basicInfo, dob: v });
    }
  };

  // Helper to handle text input changes
  const handleChange = (e) => {
    let { name, value } = e.target;
    
    if (name === "phone") {
      // Keep only digits
      let cleaned = value.replace(/\D/g, "");
      // Limit to 10 digits
      if (cleaned.length > 10) cleaned = cleaned.slice(0, 10);

      // Format as (123) 456-7890
      const part1 = cleaned.slice(0, 3);
      const part2 = cleaned.slice(3, 6);
      const part3 = cleaned.slice(6, 10);

      if (cleaned.length > 6) {
        value = `(${part1}) ${part2}-${part3}`;
      } else if (cleaned.length > 3) {
        value = `(${part1}) ${part2}`;
      } else if (cleaned.length > 0) {
        value = `(${part1}`;
      }
    }
    
    // Reset active domain if the email field no longer includes it
    if (name === "email" && !value.includes(activeDomain)) {
      setActiveDomain("");
    }

    if (name === "dob") {
      return;
    }

    const updatedBasicInfo = { ...basicInfo, [name]: value };
    onChange(updatedBasicInfo);

    // If the phone field was just updated and now has 14 characters (complete phone number),
    // and all other required fields are filled, trigger the patient search
    if (name === "phone" && value.length === 14 && !hasSearched) {
      const hasRequiredFields = 
        updatedBasicInfo.firstName && 
        updatedBasicInfo.lastName && 
        updatedBasicInfo.dob && 
        updatedBasicInfo.email;
      
      if (hasRequiredFields) {
        console.log("🔍 All required fields complete. Starting patient search...");
        checkForExistingPatient();
        setHasSearched(true);
      }
    }
  };

  // Helper to quickly append a domain if user hasn't typed '@' yet
  const handleDomainClick = (domain) => {
    const email = basicInfo.email || "";
    if (!email.includes("@")) {
      onChange({ ...basicInfo, email: email + domain });
      setActiveDomain(domain);
    }
  };

  // Function to check for possible patient matches in Firebase
  const checkForExistingPatient = async () => {
    console.log("🔍 Starting patient match check...");
    
    // Log search criteria
    console.log("📋 Search criteria:", {
      firstName: basicInfo.firstName || "(empty)",
      lastName: basicInfo.lastName || "(empty)",
      dob: basicInfo.dob || "(empty)",
      email: basicInfo.email || "(empty)",
      phone: basicInfo.phone || "(empty)"
    });
    
    if (!basicInfo.firstName || !basicInfo.lastName || !basicInfo.dob || 
        !basicInfo.email || !basicInfo.phone) {
      console.log("❌ Not all required fields are provided. Aborting search.");
      return [];
    }

    try {
      console.log("📚 Initializing database collection reference...");
      const patientsRef = collection(db, "patients");
      let matches = [];
      
      // Search by email if provided
      if (basicInfo.email) {
        console.log(`📧 Searching by email: ${basicInfo.email}`);
        const emailQuery = query(patientsRef, where("email", "==", basicInfo.email));
        console.log("⏳ Executing email query...");
        const emailSnapshot = await getDocs(emailQuery);
        
        console.log(`📊 Email query returned ${emailSnapshot.size} results`);
        emailSnapshot.forEach(doc => {
          console.log(`📄 Found document by email with ID: ${doc.id}`);
          matches.push({ id: doc.id, ...doc.data(), matchType: 'email' });
        });
      } else {
        console.log("📧 Email not provided, skipping email search");
      }
      
      // Search by phone if provided
      if (basicInfo.phone) {
        console.log(`📱 Searching by phone: ${basicInfo.phone}`);
        const phoneQuery = query(patientsRef, where("phone", "==", basicInfo.phone));
        console.log("⏳ Executing phone query...");
        const phoneSnapshot = await getDocs(phoneQuery);
        
        console.log(`📊 Phone query returned ${phoneSnapshot.size} results`);
        
        let newMatches = 0;
        let duplicates = 0;
        
        phoneSnapshot.forEach(doc => {
          // Avoid duplicates
          if (!matches.some(match => match.id === doc.id)) {
            console.log(`📄 Found new document by phone with ID: ${doc.id}`);
            matches.push({ id: doc.id, ...doc.data(), matchType: 'phone' });
            newMatches++;
          } else {
            console.log(`🔄 Skipping duplicate document with ID: ${doc.id} (already found by email)`);
            duplicates++;
          }
        });
        
        console.log(`📊 Phone search stats: ${newMatches} new matches, ${duplicates} duplicates skipped`);
      } else {
        console.log("📱 Phone not provided, skipping phone search");
      }
      
      // Search by name and DOB combination if both provided
      if (basicInfo.firstName && basicInfo.lastName && basicInfo.dob) {
        console.log(`👤 Searching by name & DOB: ${basicInfo.firstName} ${basicInfo.lastName}, ${basicInfo.dob}`);
        
        // Capitalize first letter of names
        const capitalizedFirstName = basicInfo.firstName.charAt(0).toUpperCase() + basicInfo.firstName.slice(1).toLowerCase();
        const capitalizedLastName = basicInfo.lastName.charAt(0).toUpperCase() + basicInfo.lastName.slice(1).toLowerCase();
        
        // Format DOB to match database format (M/D/YYYY)
        const formattedDob = formatDate(basicInfo.dob);
        
        console.log(`👤 Searching with capitalized names & formatted DOB: ${capitalizedFirstName} ${capitalizedLastName}, ${formattedDob}`);
        
        const nameQuery = query(
          patientsRef, 
          where("firstName", "==", capitalizedFirstName),
          where("lastName", "==", capitalizedLastName),
          where("dob", "==", formattedDob)
        );

        console.log("⏳ Executing name+DOB query...");
        const nameSnapshot = await getDocs(nameQuery);
        
        console.log(`📊 Name+DOB query returned ${nameSnapshot.size} results`);
        
        let newMatches = 0;
        let duplicates = 0;
        
        nameSnapshot.forEach(doc => {
          // Avoid duplicates
          if (!matches.some(match => match.id === doc.id)) {
            console.log(`📄 Found new document by name+DOB with ID: ${doc.id}`);
            matches.push({ id: doc.id, ...doc.data(), matchType: 'name+dob' });
            newMatches++;
          } else {
            console.log(`🔄 Skipping duplicate document with ID: ${doc.id} (already found by email or phone)`);
            duplicates++;
          }
        });
        
        console.log(`📊 Name+DOB search stats: ${newMatches} new matches, ${duplicates} duplicates skipped`);
      } else {
        console.log("👤 Complete name and DOB not provided, skipping name+DOB search");
      }
      
      console.log(`✅ Search complete. Total matches found: ${matches.length}`);
      
      if (matches.length > 0) {
        console.log("📋 Match details:", matches);
        setPossibleMatches(matches);
        setShowMatchModal(true);
      }
      
      // Store matches for later use
      setPossibleMatches(matches);
      
      return matches;
    } catch (error) {
      console.error("❌ Error checking for patient matches:", error);
      console.error("Stack trace:", error.stack);
      return [];
    } finally {
      console.log("🏁 Patient match check process completed");
    }
  };

  // Handle patient verification from modal
  const handlePatientVerify = (patient) => {
    console.log("✅ Patient verified:", patient);
    setVerifiedPatient(patient);
    
    // Auto-fill form fields with the verified patient's data
    const updatedBasicInfo = {
      ...basicInfo,
      email: patient.email || basicInfo.email,
      phone: patient.phone || basicInfo.phone
    };
    
    // Update the form data
    onChange(updatedBasicInfo);
    
    // Report the verification results, including full patient data to pre-fill demographics
    if (onCheckPatientMatches && typeof onCheckPatientMatches === 'function') {
      onCheckPatientMatches({
        type: 'MATCH_VERIFIED',
        matches: possibleMatches,
        verifiedPatient: patient,
        // Include demographics info for pre-filling
        demographicsData: {
          address: patient.address || '',
          rawAddress: patient.rawAddress || '',
          street: patient.street || '',
          city: patient.city || '',
          state: patient.state || '',
          zip: patient.zip || '',
          cityState: patient.cityState || '',
          cityStateZip: patient.cityStateZip || '',
          gender: patient.gender || '',
          race: patient.race || '',
          maritalStatus: patient.maritalStatus || '',
          ethnicity: patient.ethnicity || '',
          income: patient.income || '',
          familySize: patient.familySize || ''
        }
      });
    }
  };

  // Expose the check function to parent component
  useEffect(() => {
    if (onCheckPatientMatches && typeof onCheckPatientMatches === 'function') {
      console.log("🔄 Setting up patient match check function in parent component");
      // Pass the function with a type indicator to help the parent component distinguish this call
      onCheckPatientMatches({
        type: 'FUNCTION_REFERENCE',
        checkFunction: checkForExistingPatient
      });
    }
  }, []); // Only run once on component mount

  return (
    <div style={{ fontFamily: "Quicksand, sans-serif", marginBottom: "5rem" }}>
      
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
        <h2 style={{ margin: 0 }}>Basic Information</h2>
        <p style={{ margin: 0, color: "#777" }}>Please provide your basic details</p>
      </div>

      {/* Row: First Name, Last Name, DOB */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginBottom: "1rem" }}>
        
        {/* First Name */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <label htmlFor="firstName" style={{ marginBottom: "0.3rem", color: "#666" }}>
            First Name
          </label>
          <input
            id="firstName"
            name="firstName"
            type="text"
            value={basicInfo.firstName || ""}
            onChange={handleChange}
            style={{
              padding: "0.5rem",
              borderRadius: "8px",
              border: "1px solid #ccc",
              fontFamily: "inherit",
              textTransform: "capitalize",
              backgroundColor: "#ffffff",
              color: "#333333",
              colorScheme: "light only"
            }}
          />
        </div>
        
        {/* Last Name */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <label htmlFor="lastName" style={{ marginBottom: "0.3rem", color: "#666" }}>
            Last Name
          </label>
          <input
            id="lastName"
            name="lastName"
            type="text"
            value={basicInfo.lastName || ""}
            onChange={handleChange}
            style={{
              padding: "0.5rem",
              borderRadius: "8px",
              border: "1px solid #ccc",
              fontFamily: "inherit",
              textTransform: "capitalize",
              backgroundColor: "#ffffff",
              color: "#333333",
              colorScheme: "light only"
            }}
          />
        </div>
        
        {/* DOB: free text (parsed) or calendar */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <span style={{ marginBottom: "0.3rem", color: "#666" }}>Date of birth</span>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "center" }}>
            <input
              id="dob"
              name="dob"
              type="text"
              autoComplete="bday"
              value={dobText}
              onChange={handleDobTextChange}
              onBlur={handleDobBlur}
              placeholder="e.g. 3/15/1990 or 1990-03-15"
              style={{
                flex: 1,
                minWidth: "9rem",
                padding: "0.5rem",
                borderRadius: "8px",
                border: ageError || dobTypeError ? "1px solid red" : "1px solid #ccc",
                fontFamily: "inherit",
                backgroundColor: "#ffffff",
                color: "#333333",
                colorScheme: "light only",
              }}
            />
            <input
              type="date"
              value={basicInfo.dob || ""}
              onChange={handleDobPickerChange}
              max={new Date().toISOString().slice(0, 10)}
              min="1900-01-01"
              aria-label="Open calendar to pick date of birth"
              style={{
                padding: "0.35rem 0.5rem",
                borderRadius: "8px",
                border: "1px solid #ccc",
                fontFamily: "inherit",
                backgroundColor: "#ffffff",
                color: "#333333",
                colorScheme: "light only",
                cursor: "pointer",
              }}
            />
          </div>
          <p style={{ color: "#888", fontSize: "0.75rem", margin: "0.3rem 0 0" }}>
            Type the date in any common format, or use the calendar.
          </p>
          {dobTypeError && (
            <div style={{ color: "red", fontSize: "0.8rem", marginTop: "0.3rem" }}>{dobTypeError}</div>
          )}
          {ageError && (
            <div style={{ color: "red", fontSize: "0.8rem", marginTop: "0.3rem" }}>
              You must be at least 18 years old to continue.
            </div>
          )}
        </div>
      </div>

      {/* Contact Info */}
      <div style={{ marginBottom: "1rem" }}>
        <h4 style={{ margin: "0 0 0.5rem", color: "#777" }}>Contact Info</h4>
        
        {/* Email field + domain shortcut buttons */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "1rem" }}>
          <label htmlFor="email" style={{ color: "#666" }}>Email</label>
          <input
            id="email"
            name="email"
            type="email"
            value={basicInfo.email || ""}
            onChange={handleChange}
            style={{
              padding: "0.5rem",
              borderRadius: "8px",
              border: "1px solid #ccc",
              fontFamily: "inherit",
              backgroundColor: "#ffffff",
              color: "#333333",
              colorScheme: "light only"
            }}
          />
          <div style={{ display: "flex", flexWrap: "wrap",  gap: "0.5rem" }}>
            {["@gmail.com", "@hotmail.com", "@yahoo.com", "@outlook.com", "@icloud.com"].map((domain) => (
              <button
                key={domain}
                type="button"
                onClick={() => handleDomainClick(domain)}
                style={{
                  padding: "0.3rem 0.6rem",
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                  backgroundColor: activeDomain === domain ? UMSSBlueDark : "#f3f3f3",
                  color: activeDomain === domain ? "#fff" : "#000",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {domain}
              </button>
            ))}
          </div>
        </div>
        
        {/* Phone field */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <label htmlFor="phone" style={{ color: "#666" }}>Phone</label>
          <input
            id="phone"
            name="phone"
            type="text"
            value={basicInfo.phone || ""}
            onChange={handleChange}
            style={{
              padding: "0.5rem",
              borderRadius: "8px",
              border: "1px solid #ccc",
              fontFamily: "inherit",
              backgroundColor: "#ffffff",
              color: "#333333",
              colorScheme: "light only"
            }}
          />
        </div>
      </div>

      {/* Reason for Visit */}
      <div style={{ marginBottom: "1rem", flexDirection: "column", gap: "0.5rem" }}>
        <label htmlFor="reason" style={{ color: "#666" }}>Reason for Visit</label>
        <textarea
          id="reason"
          name="reason"
          rows="3"
          value={basicInfo.reason || ""}
          onChange={handleChange}
          style={{
            width: "100%",
            padding: "0.5rem 1rem",
            marginTop: "0.5rem",
            borderRadius: "8px",
            border: "1px solid #ccc",
            fontFamily: "inherit",
            boxSizing: "border-box",
            backgroundColor: "#ffffff",
            color: "#333333",
            colorScheme: "light only"
          }}
        />
      </div>

      {/* Patient Match Modal */}
      {showMatchModal && (
        <PatientMatchModal 
          matches={possibleMatches}
          onClose={() => setShowMatchModal(false)}
          onVerify={handlePatientVerify}
        />
      )}

    </div>
  );
}

export default BasicInfo;